import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Friends,
  type FriendCellarView,
  type FriendCheckinsView,
  type FriendShareLevel,
} from '@/lib/api';
import { Badge, Card, EmptyState, Icon, Skeleton, Spinner } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';

/**
 * Read-only viewer for a friend's bar.
 *
 * Each section (cellar, tastings) is rendered with the friend's chosen
 * share level — `hidden` short-circuits to a "not shared" empty state,
 * `counts` shows just the aggregate, and `full` renders the items.
 *
 * 404s from the backend are treated as "hidden" so we don't leak whether
 * the section exists; a 404 is functionally equivalent to a hidden share.
 */
export function FriendBarPage() {
  const { userId = '' } = useParams();

  const cellarQ = useQuery({
    queryKey: ['friend.cellar', userId],
    queryFn: () => Friends.cellar(userId),
    enabled: Boolean(userId),
    retry: false,
  });

  const checkinsQ = useQuery({
    queryKey: ['friend.checkins', userId],
    queryFn: () => Friends.checkins(userId),
    enabled: Boolean(userId),
    retry: false,
  });

  // Friend display name — pulled from whichever query lands first; both
  // include the same `friend.displayName`. Fallback to user id if neither
  // succeeds.
  const friendDisplayName =
    cellarQ.data?.friend.displayName ??
    checkinsQ.data?.friend.displayName ??
    null;

  return (
    <div className="grid gap-6">
      <div className="grid gap-3">
        <Link
          to="/friends"
          className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle"
        >
          <Icon name="chevron-left" size={14} /> Tilbake til vennelisten
        </Link>
        <header className="grid gap-1">
          <h1 className="ui-section-title text-3xl">
            {friendDisplayName ? `${friendDisplayName}s bar` : 'Vennens bar'}
          </h1>
          <p className="text-sm text-mist">
            Du ser bare det vennen din har valgt å dele med deg.
          </p>
        </header>
      </div>

      <FriendCellarSection data={cellarQ.data} loading={cellarQ.isLoading} error={cellarQ.isError} />
      <FriendCheckinsSection data={checkinsQ.data} loading={checkinsQ.isLoading} error={checkinsQ.isError} />
    </div>
  );
}

function SectionHeader({
  icon,
  title,
  level,
  total,
}: {
  icon: 'bottle' | 'check-circle';
  title: string;
  level: FriendShareLevel | null;
  total?: number;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Icon name={icon} className="text-candle" />
        <h2 className="ui-section-title text-xl">{title}</h2>
        {total != null && <Badge tone="mist">{total}</Badge>}
      </div>
      {level && <ShareBadge level={level} />}
    </div>
  );
}

function ShareBadge({ level }: { level: FriendShareLevel }) {
  if (level === 'full') return <Badge tone="ruby">deler alt</Badge>;
  if (level === 'counts') return <Badge tone="gold">deler antall</Badge>;
  return <Badge tone="mist">skjult</Badge>;
}

function FriendCellarSection({
  data,
  loading,
  error,
}: {
  data?: FriendCellarView;
  loading: boolean;
  error: boolean;
}) {
  if (loading) {
    return (
      <Card className="grid gap-3 p-5">
        <SectionHeader icon="bottle" title="Kjeller" level={null} />
        <div className="flex items-center gap-2 text-sm text-mist"><Spinner /> Laster…</div>
        <Skeleton height={48} /><Skeleton height={48} />
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="grid gap-3 p-5">
        <SectionHeader icon="bottle" title="Kjeller" level="hidden" />
        <p className="text-sm text-mist">Vennen din deler ikke kjelleren med deg.</p>
      </Card>
    );
  }
  return (
    <Card className="grid gap-4 p-5">
      <SectionHeader icon="bottle" title="Kjeller" level={data.level} total={data.counts.total} />

      {data.level === 'counts' && (
        <div className="rounded-md bg-ink-soft/60 p-4 text-sm text-parchment-dim">
          {data.counts.total === 0
            ? 'Ingen flasker i kjelleren akkurat nå.'
            : `${data.counts.total} flasker totalt — be om "alt"-tilgang for å se hver enkelt.`}
        </div>
      )}

      {data.level === 'full' && (data.bottles?.length ?? 0) === 0 && (
        <EmptyState
          icon="bottle-empty"
          title="Tom kjeller"
          description="Vennen din har ingen flasker akkurat nå."
        />
      )}

      {data.level === 'full' && data.bottles && data.bottles.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
        >
          {data.bottles.map((b) => (
            <div key={b.id} className="ui-card grid gap-2 p-3">
              <div className="relative grid h-28 place-items-center overflow-hidden rounded bg-ink-soft xl:h-32">
                <BottleImage src={b.photoUrl} alt={b.name} iconSize={32} mode="lightbox" />
                {b.qty > 1 && (
                  <span className="absolute bottom-2 right-2 ui-badge ui-badge--ruby">×{b.qty}</span>
                )}
              </div>
              <div className="grid gap-1">
                <h3 className="font-serif text-sm leading-tight text-parchment line-clamp-2">{b.name}</h3>
                <div className="flex items-center gap-2 text-[11px] text-mist">
                  {b.region && <span className="truncate">{b.region}</span>}
                  {b.vintage && (
                    <>
                      <span aria-hidden>·</span>
                      <span>{b.vintage}</span>
                    </>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {b.tags.slice(0, 2).map((t) => (
                    <Badge key={t} tone="mist">
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </Card>
  );
}

function FriendCheckinsSection({
  data,
  loading,
  error,
}: {
  data?: FriendCheckinsView;
  loading: boolean;
  error: boolean;
}) {
  if (loading) {
    return (
      <Card className="grid gap-3 p-5">
        <SectionHeader icon="check-circle" title="Smakinger" level={null} />
        <Skeleton height={56} /><Skeleton height={56} />
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="grid gap-3 p-5">
        <SectionHeader icon="check-circle" title="Smakinger" level="hidden" />
        <p className="text-sm text-mist">Vennen din deler ikke smakinger med deg.</p>
      </Card>
    );
  }
  return (
    <Card className="grid gap-4 p-5">
      <SectionHeader
        icon="check-circle"
        title="Smakinger"
        level={data.level}
        total={data.counts.total}
      />

      {data.level === 'counts' && (
        <div className="rounded-md bg-ink-soft/60 p-4 text-sm text-parchment-dim">
          {data.counts.total === 0
            ? 'Ingen smakinger enda.'
            : `${data.counts.total} smakinger totalt.`}
        </div>
      )}

      {data.level === 'full' && (data.checkins?.length ?? 0) === 0 && (
        <EmptyState icon="check-circle" title="Ingen smakinger enda" />
      )}

      {data.level === 'full' && data.checkins && data.checkins.length > 0 && (
        <ul className="grid gap-3">
          {data.checkins.map((c) => (
            <li key={c.id} className="ui-card grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3">
              <div className="relative grid h-14 w-10 place-items-center overflow-hidden rounded bg-ink-soft">
                <BottleImage
                  src={c.snapshotImageUrl}
                  iconSize={18}
                />
              </div>
              <div className="grid">
                <span className="font-serif text-sm text-parchment line-clamp-1">
                  {c.snapshotName}
                </span>
                <span className="text-[11px] text-mist line-clamp-1">
                  {[c.snapshotRegion, c.snapshotVintage, c.venueName].filter(Boolean).join(' · ')}
                </span>
                {c.comment && (
                  <span className="mt-1 text-xs text-parchment-dim line-clamp-2">{c.comment}</span>
                )}
              </div>
              <div className="grid gap-1 text-right">
                {c.rating != null && (
                  <span className="font-serif text-lg text-candle">{c.rating.toFixed(1)}</span>
                )}
                <span className="text-[10px] uppercase tracking-widest text-mist">
                  {new Date(c.createdAt).toLocaleDateString('nb-NO')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
